export type PickDenguChannel = "web" | "whatsapp";

const CHANNEL_STORAGE_KEY = "pickdenguCheckoutChannel";
const CHANNEL_TTL_MS = 12 * 60 * 60 * 1000;

type StoredChannel = {
  channel?: string;
  expiresAt?: number;
};

const normalizeChannel = (
  value: string | null | undefined,
): PickDenguChannel | null => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();

  if (["whatsapp", "wa"].includes(normalized)) {
    return "whatsapp";
  }

  if (["web", "browser"].includes(normalized)) {
    return "web";
  }

  return null;
};

const isWhatsAppUserAgent = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  return /whatsapp/i.test(window.navigator.userAgent || "");
};

const saveChannel = (channel: PickDenguChannel): void => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(
    CHANNEL_STORAGE_KEY,
    JSON.stringify({
      channel,
      expiresAt: Date.now() + CHANNEL_TTL_MS,
    }),
  );
};

const readStoredChannel = (): PickDenguChannel | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(CHANNEL_STORAGE_KEY);

    if (!rawValue) {
      return null;
    }

    /*
     * Keep compatibility with any earlier implementation that saved only
     * the string "whatsapp" instead of a JSON object.
     */
    const legacyChannel = normalizeChannel(rawValue);

    if (legacyChannel) {
      saveChannel(legacyChannel);
      return legacyChannel;
    }

    const parsed = JSON.parse(rawValue) as StoredChannel;
    const channel = normalizeChannel(parsed.channel);
    const expiresAt = Number(parsed.expiresAt);

    if (!channel || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      window.sessionStorage.removeItem(CHANNEL_STORAGE_KEY);
      return null;
    }

    return channel;
  } catch {
    window.sessionStorage.removeItem(CHANNEL_STORAGE_KEY);
    return null;
  }
};

const getExplicitChannel = (search: string): PickDenguChannel | null => {
  const params = new URLSearchParams(search);

  return (
    normalizeChannel(params.get("channel")) ||
    normalizeChannel(params.get("source")) ||
    normalizeChannel(params.get("checkoutSource"))
  );
};

export const capturePickDenguChannel = ({
  pathname,
  search,
}: {
  pathname: string;
  search: string;
}): PickDenguChannel => {
  if (typeof window === "undefined") {
    return "web";
  }

  const explicitChannel = getExplicitChannel(search);

  if (explicitChannel) {
    saveChannel(explicitChannel);
    return explicitChannel;
  }

  if (/^\/whatsapp(?:\/|$)/i.test(pathname)) {
    saveChannel("whatsapp");
    return "whatsapp";
  }

  if (isWhatsAppUserAgent()) {
    saveChannel("whatsapp");
    return "whatsapp";
  }

  return readStoredChannel() || "web";
};

export const getPickDenguChannel = (): PickDenguChannel => {
  if (typeof window === "undefined") {
    return "web";
  }

  return capturePickDenguChannel({
    pathname: window.location.pathname,
    search: window.location.search,
  });
};

export const isWhatsAppChannel = (): boolean => {
  return getPickDenguChannel() === "whatsapp";
};

export const withPickDenguChannel = (value: string): string => {
  const originalValue = String(value || "").trim();

  if (!originalValue || !isWhatsAppChannel()) {
    return originalValue;
  }

  if (typeof window === "undefined") {
    return originalValue;
  }

  try {
    const parsedUrl = new URL(originalValue, window.location.origin);

    /*
     * Never alter external URLs. This helper is only for PickDengu's own
     * React routes.
     */
    if (parsedUrl.origin !== window.location.origin) {
      return originalValue;
    }

    parsedUrl.searchParams.set("channel", "whatsapp");

    if (/^https?:\/\//i.test(originalValue)) {
      return parsedUrl.toString();
    }

    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch {
    return originalValue;
  }
};

export const clearPickDenguChannel = (): void => {
  if (typeof window !== "undefined") {
    window.sessionStorage.removeItem(CHANNEL_STORAGE_KEY);
  }
};
